import Phaser from 'phaser';

export interface DecorVariant {
  key: string;
  width: number;
  height: number;
  weight: number;
  scaleMin: number;
  scaleMax: number;
  draw: (g: Phaser.GameObjects.Graphics, w: number, h: number) => void;
}

export function ensureDecorTextures(scene: Phaser.Scene, variants: DecorVariant[]): void {
  for (const v of variants) {
    if (scene.textures.exists(v.key)) continue;
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    v.draw(g, v.width, v.height);
    g.generateTexture(v.key, v.width, v.height);
    g.destroy();
  }
}

// ---------------------------------------------------------------- outdoor

export function drawOakTree(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.65, 7);

  // Trunk
  g.fillStyle(0x6b3f1f, 1);
  g.fillRect(w / 2 - 4, h - 22, 8, 16);
  g.fillStyle(0x4a2810, 1);
  g.fillRect(w / 2 - 4, h - 22, 2, 16);

  const cx = w / 2;
  // Canopy fills the upper portion of the texture; the topmost circle
  // (radius 14 above cy by 14) needs cy >= 28 to avoid clipping.
  const cy = 30;

  // Outer canopy (darkest) — drawn slightly larger than mid for outline
  g.fillStyle(0x183010, 1);
  g.fillCircle(cx, cy, 22);
  g.fillCircle(cx - 14, cy + 4, 16);
  g.fillCircle(cx + 14, cy + 4, 16);
  g.fillCircle(cx, cy - 14, 14);
  g.fillCircle(cx - 10, cy - 10, 11);
  g.fillCircle(cx + 10, cy - 10, 11);

  // Mid green
  g.fillStyle(0x2d5a1c, 1);
  g.fillCircle(cx, cy, 20);
  g.fillCircle(cx - 13, cy + 4, 14);
  g.fillCircle(cx + 13, cy + 4, 14);
  g.fillCircle(cx, cy - 13, 12);
  g.fillCircle(cx - 9, cy - 9, 9);
  g.fillCircle(cx + 9, cy - 9, 9);

  // Highlights
  g.fillStyle(0x4f7f2f, 1);
  g.fillCircle(cx - 6, cy - 6, 10);
  g.fillCircle(cx + 8, cy - 2, 7);
  g.fillStyle(0x6da93f, 1);
  g.fillCircle(cx - 4, cy - 8, 4);
}

export function drawPineTree(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.55, 6);

  // Trunk
  g.fillStyle(0x3a2010, 1);
  g.fillRect(w / 2 - 3, h - 14, 6, 10);
  g.fillStyle(0x6b3f1f, 1);
  g.fillRect(w / 2 - 3, h - 14, 2, 10);

  const cx = w / 2;
  const tipY = 2;
  const baseY = h - 14;
  const canopyH = baseY - tipY;

  // Three stacked layers, widest at the bottom (proper pine silhouette).
  // Each layer is a triangle whose apex sits above the previous layer's base.
  const layers = [
    { widthFactor: 0.5, fill: 0x254a18, edge: 0x0a1a04 },
    { widthFactor: 0.75, fill: 0x2d5a1c, edge: 0x122908 },
    { widthFactor: 1.0, fill: 0x356c20, edge: 0x183010 }
  ];
  const halfMax = w / 2 - 2;

  for (let i = 0; i < layers.length; i++) {
    const apexY = tipY + i * canopyH * 0.32;
    const baseLayerY = apexY + canopyH * 0.55;
    const halfW = halfMax * layers[i].widthFactor;
    // Outline
    g.fillStyle(layers[i].edge, 1);
    g.fillTriangle(cx, apexY - 1, cx - halfW - 1, baseLayerY + 1, cx + halfW + 1, baseLayerY + 1);
    // Fill
    g.fillStyle(layers[i].fill, 1);
    g.fillTriangle(cx, apexY, cx - halfW, baseLayerY, cx + halfW, baseLayerY);
  }

  // Vertical highlight on the left face of each layer
  g.fillStyle(0x6da93f, 0.8);
  g.fillTriangle(cx, tipY + 2, cx - 3, tipY + canopyH * 0.4, cx, tipY + canopyH * 0.35);
}

export function drawBush(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.7, 4);

  // Outline
  g.fillStyle(0x183010, 1);
  g.fillEllipse(w * 0.3, h * 0.55, 16, 13);
  g.fillEllipse(w * 0.7, h * 0.55, 16, 13);
  g.fillEllipse(w * 0.5, h * 0.42, 18, 15);

  // Mid
  g.fillStyle(0x4ca035, 1);
  g.fillEllipse(w * 0.3, h * 0.55, 13, 10);
  g.fillEllipse(w * 0.7, h * 0.55, 13, 10);
  g.fillEllipse(w * 0.5, h * 0.42, 15, 12);

  // Highlights
  g.fillStyle(0x7ec048, 1);
  g.fillCircle(w * 0.5, h * 0.4 - 2, 4);
  g.fillCircle(w * 0.32, h * 0.5, 2);
}

export function drawFlowerRed(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  drawFlower(g, w, h, 0xdc2626, 0x7f1d1d);
}

export function drawFlowerYellow(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  drawFlower(g, w, h, 0xfbbf24, 0xa16207);
}

export function drawFlowerWhite(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  drawFlower(g, w, h, 0xfafafa, 0x9ca3af);
}

function drawFlower(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  petalColor: number,
  petalEdge: number
): void {
  const cx = w / 2;
  // Stem
  g.fillStyle(0x2d5a1c, 1);
  g.fillRect(cx - 1, h * 0.45, 2, h * 0.55);
  // Leaf
  g.fillEllipse(cx + 4, h * 0.7, 6, 3);
  // Petals
  g.fillStyle(petalEdge, 1);
  g.fillCircle(cx, h * 0.28, 4);
  g.fillCircle(cx - 4, h * 0.4, 4);
  g.fillCircle(cx + 4, h * 0.4, 4);
  g.fillCircle(cx, h * 0.52, 4);
  g.fillStyle(petalColor, 1);
  g.fillCircle(cx, h * 0.28, 3);
  g.fillCircle(cx - 4, h * 0.4, 3);
  g.fillCircle(cx + 4, h * 0.4, 3);
  g.fillCircle(cx, h * 0.52, 3);
  // Center
  g.fillStyle(0xfbbf24, 1);
  g.fillCircle(cx, h * 0.4, 2);
}

export function drawRock(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.7, 3);

  g.fillStyle(0x52525b, 1);
  g.fillEllipse(w * 0.5, h * 0.65, w * 0.9, h * 0.7);

  g.fillStyle(0x9ca3af, 1);
  g.fillEllipse(w * 0.5, h * 0.55, w * 0.85, h * 0.6);
  g.fillEllipse(w * 0.35, h * 0.45, w * 0.5, h * 0.45);

  g.fillStyle(0xd1d5db, 1);
  g.fillEllipse(w * 0.4, h * 0.38, 6, 3);
}

export function drawMossyRock(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  drawRock(g, w, h);
  g.fillStyle(0x4ca035, 1);
  g.fillEllipse(w * 0.4, h * 0.28, 9, 4);
  g.fillEllipse(w * 0.65, h * 0.32, 5, 2);
  g.fillStyle(0x7ec048, 1);
  g.fillEllipse(w * 0.4, h * 0.27, 5, 2);
}

export function drawStump(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.85, 3);

  // Side
  g.fillStyle(0x4a2810, 1);
  g.fillEllipse(w / 2, h * 0.75, w - 4, h * 0.4);

  // Top
  g.fillStyle(0x7c5a2c, 1);
  g.fillEllipse(w / 2, h * 0.5, w - 4, h * 0.45);

  // Rings
  g.lineStyle(1, 0x4a2810, 1);
  g.strokeEllipse(w / 2, h * 0.5, (w - 4) / 1.6, h * 0.3);
  g.strokeEllipse(w / 2, h * 0.5, (w - 4) / 2.6, h * 0.2);
}

export function drawTallGrass(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  g.lineStyle(1.5, 0x2d5a1c, 1);
  for (let i = 0; i < 5; i++) {
    const x = (i + 0.5) * (w / 5);
    g.lineBetween(x, h, x - 1.5, h * 0.2);
  }
  g.lineStyle(1.5, 0x4ca035, 1);
  for (let i = 0; i < 4; i++) {
    const x = (i + 1) * (w / 5);
    g.lineBetween(x, h, x + 1.5, h * 0.35);
  }
}

// ---------------------------------------------------------------- city

// Pseudo-random lit/unlit window pattern; deterministic per (row,col)
function windowLit(row: number, col: number, salt = 0): boolean {
  return ((row * 73856093) ^ (col * 19349663) ^ (salt * 83492791)) % 7 < 4;
}

export function drawSkyscraper(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.95, 4);

  // Antenna mast
  g.fillStyle(0x1a2433, 1);
  g.fillRect(w / 2 - 0.5, 0, 1, 6);
  g.fillStyle(0xb91c1c, 1);
  g.fillRect(w / 2 - 1, 2, 2, 1);

  // Crown / setback
  g.fillStyle(0x1a2433, 1);
  g.fillRect(w * 0.25, 5, w * 0.5, 5);

  // Main tower body — dark frame
  g.fillStyle(0x111827, 1);
  g.fillRect(0, 8, w, h - 10);
  // Glass facade
  g.fillStyle(0x3b5876, 1);
  g.fillRect(2, 9, w - 4, h - 12);

  // Vertical mullions
  g.fillStyle(0x111827, 1);
  const mullions = 4;
  for (let i = 1; i < mullions; i++) {
    const x = 2 + (i * (w - 4)) / mullions;
    g.fillRect(Math.floor(x), 9, 1, h - 12);
  }

  // Horizontal floor slabs + lit windows
  const floorH = 4;
  const floors = Math.floor((h - 14) / floorH);
  for (let f = 0; f < floors; f++) {
    const y = 10 + f * floorH;
    // Slab line
    g.fillStyle(0x1a2a3b, 1);
    g.fillRect(2, y + floorH - 1, w - 4, 1);
    // Per-cell glass tint
    for (let c = 0; c < mullions; c++) {
      const cellX = 2 + (c * (w - 4)) / mullions + 1;
      const cellW = (w - 4) / mullions - 1;
      const lit = windowLit(f, c, 1);
      g.fillStyle(lit ? 0xfde68a : 0x6b89a8, 1);
      g.fillRect(Math.floor(cellX), y, Math.ceil(cellW), floorH - 1);
    }
  }

  // Edge highlight for glassiness
  g.fillStyle(0xa3c3e0, 0.35);
  g.fillRect(3, 9, 1, h - 12);
}

export function drawOfficeTower(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.95, 4);

  // Roof parapet
  g.fillStyle(0x2a211a, 1);
  g.fillRect(0, 0, w, 3);
  // Body
  g.fillStyle(0x3d2f23, 1);
  g.fillRect(0, 3, w, h - 5);
  g.fillStyle(0x5a4530, 1);
  g.fillRect(1, 4, w - 2, h - 7);

  // Window grid
  const cols = 4;
  const rowH = 5;
  const rows = Math.floor((h - 10) / rowH);
  const cellW = (w - 6) / cols;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = 3 + c * cellW;
      const y = 6 + r * rowH;
      // Frame
      g.fillStyle(0x2a211a, 1);
      g.fillRect(Math.floor(x), y, Math.ceil(cellW) - 1, rowH - 1);
      // Glass
      const lit = windowLit(r, c, 2);
      g.fillStyle(lit ? 0xfde68a : 0x1f2a3a, 1);
      g.fillRect(Math.floor(x) + 1, y + 1, Math.ceil(cellW) - 3, rowH - 3);
    }
  }
}

export function drawBrownstone(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.95, 3);

  // Cornice
  g.fillStyle(0x3a1408, 1);
  g.fillRect(0, 0, w, 3);
  g.fillStyle(0x6b2f1a, 1);
  g.fillRect(1, 1, w - 2, 1);

  // Body — brick
  g.fillStyle(0x3a1408, 1);
  g.fillRect(0, 3, w, h - 5);
  g.fillStyle(0x7d2f1a, 1);
  g.fillRect(1, 3, w - 2, h - 7);

  // Brick courses
  g.fillStyle(0x5a1f10, 0.5);
  for (let y = 6; y < h - 6; y += 4) {
    g.fillRect(1, y, w - 2, 1);
  }

  // Windows — two rows of two
  for (let row = 0; row < 2; row++) {
    const yBase = 5 + row * 10;
    for (const wx of [w * 0.15, w * 0.6]) {
      g.fillStyle(0x1a0a04, 1);
      g.fillRect(wx, yBase, 7, 7);
      g.fillStyle(0xb8d4eb, 1);
      g.fillRect(wx + 1, yBase + 1, 5, 5);
      // Cross mullion
      g.fillStyle(0x1a0a04, 1);
      g.fillRect(wx + 3, yBase + 1, 1, 5);
      g.fillRect(wx + 1, yBase + 3, 5, 1);
    }
  }

  // Stoop steps
  g.fillStyle(0x6b6b6b, 1);
  g.fillRect(w * 0.32, h - 2, w * 0.36, 2);
  g.fillStyle(0x8a8a8a, 1);
  g.fillRect(w * 0.36, h - 2, w * 0.28, 1);

  // Door
  const dx = w * 0.4;
  const dw = w * 0.2;
  g.fillStyle(0x1a0a04, 1);
  g.fillRect(dx, h - 9, dw, 7);
  g.fillStyle(0x4a2810, 1);
  g.fillRect(dx + 1, h - 9, dw - 2, 6);
  // Door frame highlight
  g.fillStyle(0x6b3f1f, 1);
  g.fillRect(dx + 1, h - 9, dw - 2, 1);
  // Knob
  g.fillStyle(0xfbbf24, 1);
  g.fillRect(dx + dw - 2, h - 5, 1, 1);
}

export function drawShop(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.95, 3);

  // Body
  g.fillStyle(0x3a3025, 1);
  g.fillRect(0, 0, w, h - 3);
  g.fillStyle(0xe7d6b8, 1);
  g.fillRect(1, 0, w - 2, h - 5);

  // Sign band
  g.fillStyle(0x1f2937, 1);
  g.fillRect(1, 1, w - 2, 5);
  // Sign letters (dashes)
  g.fillStyle(0xfde68a, 1);
  g.fillRect(w * 0.18, 3, 3, 1);
  g.fillRect(w * 0.32, 3, 4, 1);
  g.fillRect(w * 0.5, 3, 3, 1);
  g.fillRect(w * 0.62, 3, 4, 1);
  g.fillRect(w * 0.78, 3, 3, 1);

  // Awning with stripes
  const awningY = 7;
  g.fillStyle(0xb91c1c, 1);
  g.fillRect(0, awningY, w, 3);
  g.fillStyle(0xf5f5f5, 1);
  for (let i = 0; i < Math.floor(w / 4); i++) {
    g.fillRect(i * 4 + 1, awningY, 2, 3);
  }
  // Awning lower trim
  g.fillStyle(0x7f1d1d, 1);
  g.fillRect(0, awningY + 3, w, 1);

  // Storefront window
  const winY = awningY + 5;
  const winH = h - winY - 4;
  g.fillStyle(0x1a2a3b, 1);
  g.fillRect(2, winY, w - 4, winH);
  g.fillStyle(0x9bb8d4, 1);
  g.fillRect(3, winY + 1, w - 6, winH - 2);
  // Window cross-mullion
  g.fillStyle(0x1a2a3b, 1);
  g.fillRect(w / 2 - 0.5, winY, 1, winH);

  // Door
  const dx = w * 0.42;
  const dw = w * 0.16;
  g.fillStyle(0x1a0a04, 1);
  g.fillRect(dx, h - 8, dw, 6);
  g.fillStyle(0x4a2810, 1);
  g.fillRect(dx + 1, h - 8, dw - 2, 5);
  // Sidewalk
  g.fillStyle(0xa8a29e, 1);
  g.fillRect(0, h - 2, w, 2);
}

export function drawApartment(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.95, 4);

  // Roof
  g.fillStyle(0x33302d, 1);
  g.fillRect(0, 0, w, 3);
  // Body
  g.fillStyle(0x57534e, 1);
  g.fillRect(0, 3, w, h - 5);
  g.fillStyle(0xa8a29e, 1);
  g.fillRect(1, 3, w - 2, h - 7);

  // Window grid w/ balconies
  const cols = 3;
  const rowH = 7;
  const rows = Math.floor((h - 10) / rowH);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = 3 + c * ((w - 6) / cols);
      const y = 5 + r * rowH;
      const cw = (w - 6) / cols - 1;
      // Frame
      g.fillStyle(0x3f3f46, 1);
      g.fillRect(Math.floor(x), y, Math.ceil(cw), 4);
      // Glass
      const lit = windowLit(r, c, 3);
      g.fillStyle(lit ? 0xfde68a : 0x6b7d8f, 1);
      g.fillRect(Math.floor(x) + 1, y + 1, Math.ceil(cw) - 2, 2);
      // Balcony rail
      g.fillStyle(0x3f3f46, 1);
      g.fillRect(Math.floor(x) - 1, y + 4, Math.ceil(cw) + 2, 1);
      // Balcony bars
      for (let b = 0; b < 3; b++) {
        g.fillRect(Math.floor(x) + b * 2, y + 4, 1, 2);
      }
    }
  }
}

export function drawStreetlamp(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.7, 3);
  // Base
  g.fillStyle(0x1f2937, 1);
  g.fillRect(w / 2 - 2, h - 4, 4, 3);
  // Pole
  g.fillStyle(0x111827, 1);
  g.fillRect(w / 2 - 1, 6, 2, h - 8);
  // Lamp head
  g.fillStyle(0x111827, 1);
  g.fillRect(w / 2 - 4, 3, 8, 4);
  // Bulb
  g.fillStyle(0xfde68a, 1);
  g.fillRect(w / 2 - 3, 4, 6, 2);
  // Glow
  g.fillStyle(0xfde68a, 0.3);
  g.fillCircle(w / 2, 5, 5);
  g.fillStyle(0xfde68a, 0.15);
  g.fillCircle(w / 2, 5, 8);
}

export function drawFireHydrant(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.9, 2);
  // Bottom flange
  g.fillStyle(0x7f1d1d, 1);
  g.fillRect(1, h - 3, w - 2, 2);
  // Body
  g.fillStyle(0xb91c1c, 1);
  g.fillRect(w / 2 - 3, h - 12, 6, 9);
  // Body highlight
  g.fillStyle(0xef4444, 1);
  g.fillRect(w / 2 - 3, h - 12, 1, 9);
  // Side nozzles
  g.fillStyle(0xeab308, 1);
  g.fillRect(0, h - 9, 2, 3);
  g.fillRect(w - 2, h - 9, 2, 3);
  // Top cap
  g.fillStyle(0x7f1d1d, 1);
  g.fillRect(w / 2 - 4, h - 14, 8, 2);
  g.fillStyle(0xeab308, 1);
  g.fillRect(w / 2 - 1, h - 16, 2, 2);
}

export function drawTrashBin(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.9, 2);
  // Body
  g.fillStyle(0x052e16, 1);
  g.fillRect(1, 4, w - 2, h - 6);
  g.fillStyle(0x166534, 1);
  g.fillRect(2, 4, w - 4, h - 7);
  // Vertical ridges
  g.fillStyle(0x052e16, 1);
  for (let x = 4; x < w - 3; x += 3) {
    g.fillRect(x, 5, 1, h - 9);
  }
  // Lid
  g.fillStyle(0x052e16, 1);
  g.fillRect(0, 1, w, 3);
  g.fillStyle(0x14532d, 1);
  g.fillRect(1, 2, w - 2, 1);
}

export function drawBench(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.98, 2);
  // Legs (cast iron)
  g.fillStyle(0x1f2937, 1);
  g.fillRect(2, h - 7, 2, 6);
  g.fillRect(w - 4, h - 7, 2, 6);
  // Seat slats
  g.fillStyle(0x4a2810, 1);
  g.fillRect(0, h - 8, w, 3);
  g.fillStyle(0x6b3f1f, 1);
  g.fillRect(0, h - 8, w, 1);
  g.fillRect(0, h - 6, w, 1);
  // Back rest slats
  g.fillStyle(0x4a2810, 1);
  g.fillRect(1, 2, w - 2, 2);
  g.fillRect(1, 6, w - 2, 2);
  g.fillStyle(0x6b3f1f, 1);
  g.fillRect(1, 2, w - 2, 1);
  g.fillRect(1, 6, w - 2, 1);
  // Back posts
  g.fillStyle(0x1f2937, 1);
  g.fillRect(2, 2, 1, h - 8);
  g.fillRect(w - 3, 2, 1, h - 8);
}

export function drawParkedCar(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.95, 3);
  // Lower body
  g.fillStyle(0x7f1d1d, 1);
  g.fillRect(1, h - 8, w - 2, 6);
  g.fillStyle(0xb91c1c, 1);
  g.fillRect(2, h - 8, w - 4, 5);
  // Body highlight
  g.fillStyle(0xdc2626, 1);
  g.fillRect(3, h - 7, w - 6, 1);
  // Cabin
  const cabX = w * 0.22;
  const cabW = w * 0.56;
  g.fillStyle(0x7f1d1d, 1);
  g.fillRect(cabX, h - 14, cabW, 6);
  g.fillStyle(0xdc2626, 1);
  g.fillRect(cabX, h - 14, cabW, 1);
  // Windows
  g.fillStyle(0x1e293b, 1);
  g.fillRect(cabX + 1, h - 13, cabW * 0.45, 4);
  g.fillRect(cabX + cabW * 0.55, h - 13, cabW * 0.45, 4);
  g.fillStyle(0x60a5fa, 0.6);
  g.fillRect(cabX + 1, h - 13, cabW * 0.45, 1);
  g.fillRect(cabX + cabW * 0.55, h - 13, cabW * 0.45, 1);
  // Headlight (right side)
  g.fillStyle(0xfde68a, 1);
  g.fillRect(w - 3, h - 6, 2, 2);
  // Door line
  g.fillStyle(0x4a0a0a, 1);
  g.fillRect(w / 2, h - 7, 1, 4);
  // Wheels
  g.fillStyle(0x111827, 1);
  g.fillCircle(w * 0.22, h - 2, 2.5);
  g.fillCircle(w * 0.78, h - 2, 2.5);
  g.fillStyle(0x6b7280, 1);
  g.fillCircle(w * 0.22, h - 2, 1);
  g.fillCircle(w * 0.78, h - 2, 1);
}

export function drawPlanter(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.95, 2);
  // Planter box
  g.fillStyle(0x3f3f46, 1);
  g.fillRect(0, h - 7, w, 6);
  g.fillStyle(0x71717a, 1);
  g.fillRect(1, h - 7, w - 2, 5);
  // Top rim shadow
  g.fillStyle(0x52525b, 1);
  g.fillRect(0, h - 7, w, 1);
  // Soil
  g.fillStyle(0x3a2010, 1);
  g.fillRect(2, h - 8, w - 4, 1);
  // Greenery
  g.fillStyle(0x183010, 1);
  g.fillCircle(w * 0.3, h - 9, 4);
  g.fillCircle(w * 0.65, h - 9, 4);
  g.fillCircle(w * 0.48, h - 11, 4);
  g.fillStyle(0x4ca035, 1);
  g.fillCircle(w * 0.3, h - 10, 3);
  g.fillCircle(w * 0.65, h - 10, 3);
  g.fillCircle(w * 0.48, h - 12, 3);
  g.fillStyle(0x7ec048, 1);
  g.fillCircle(w * 0.32, h - 11, 1.5);
}

export function drawTrafficCone(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.9, 2);
  // Base
  g.fillStyle(0x1f1f1f, 1);
  g.fillRect(0, h - 3, w, 2);
  // Cone shadow side
  g.fillStyle(0x9a3410, 1);
  g.fillTriangle(w / 2, 2, 1, h - 3, w - 1, h - 3);
  // Cone front
  g.fillStyle(0xea580c, 1);
  g.fillTriangle(w / 2, 3, 2, h - 3, w / 2, h - 3);
  // Reflective stripes
  g.fillStyle(0xf5f5f5, 1);
  const stripe1Y = h - 6;
  const stripe2Y = h - 9;
  // Approximate trapezoidal stripe via rectangles tapered by triangle width
  const t1Half = ((stripe1Y - 2) / (h - 5)) * (w / 2 - 1);
  const t2Half = ((stripe2Y - 2) / (h - 5)) * (w / 2 - 1);
  g.fillRect(w / 2 - t1Half, stripe1Y, t1Half * 2, 1);
  g.fillRect(w / 2 - t2Half, stripe2Y, t2Half * 2, 1);
}

// ---------------------------------------------------------------- office

export function drawDeskWood(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.95, 3);
  g.fillStyle(0x3a2010, 1);
  g.fillRect(0, 0, w, h - 2);
  g.fillStyle(0x6b3f1f, 1);
  g.fillRect(1, 0, w - 2, h - 4);
  // Drawer lines
  g.lineStyle(1, 0x3a2010, 1);
  g.lineBetween(w / 2, 4, w / 2, h - 4);
  g.lineBetween(2, h / 2, w - 2, h / 2);
}

export function drawCubicle(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
  shadow(g, w, h, w * 0.9, 3);
  g.fillStyle(0x111827, 1);
  g.fillRect(0, 0, w, h - 2);
  g.fillStyle(0x374151, 1);
  g.fillRect(1, 0, w - 2, h - 4);
  // Monitor
  g.fillStyle(0x000000, 1);
  g.fillRect(w * 0.25, h * 0.2, w * 0.5, h * 0.4);
  g.fillStyle(0x60a5fa, 1);
  g.fillRect(w * 0.27, h * 0.22, w * 0.46, h * 0.36);
}

// ---------------------------------------------------------------- helpers

function shadow(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  width: number,
  shadowH: number
): void {
  g.fillStyle(0x000000, 0.25);
  g.fillEllipse(w / 2, h - shadowH / 2 - 1, width, shadowH);
}
